import { useAutoTranslation, useUserStore } from "@/entities"
import {
  authControllerProcessResetPass,
  type ProcessResetPasswordDtoReq,
} from "@/shared/api/generated"
import { LANG_KEYS, ROUTER_PATHS } from "@/shared/constants"
import { useMutation } from "@tanstack/react-query"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { useNavigate } from "react-router-dom"

type FormData = ProcessResetPasswordDtoReq & { confirmPassword: string }
export function useResetProcessPasswordForm(code: string) {
  const navigate = useNavigate()
  const { t } = useAutoTranslation()
  const {
    handleSubmit,
    control,
    formState: { errors },
    setError,
    reset,
  } = useForm<FormData>({
    defaultValues: {
      password: "",
      confirmPassword: "",
      code: code,
    },
  })

  const login = useUserStore(s => s.login)
  const [isLoading, setIsLoading] = useState(false)

  const formMutation = useMutation({
    mutationFn: (data: ProcessResetPasswordDtoReq) =>
      authControllerProcessResetPass(data),
    async onSuccess(data) {
      await login(data.access_token)
      reset()
      navigate(ROUTER_PATHS.APP_LIBRARY, { replace: true })
      setIsLoading(false)
    },
    onError() {
      setIsLoading(false)
    },
  })

  const NOT_MATCH_PASSWORDS_ERROR = {
    type: "custom",
    message: t(LANG_KEYS.PASSWORDS_DONT_MATCH),
  }

  return {
    control,
    handleSubmit: handleSubmit(values => {
      if (values.password !== values.confirmPassword) {
        setError("password", NOT_MATCH_PASSWORDS_ERROR)
        setError("confirmPassword", NOT_MATCH_PASSWORDS_ERROR)
        return
      }
      setIsLoading(true)
      formMutation.mutate({
        password: values.password,
        code,
      })
    }),
    isLoading,
    errors,
  }
}
